import { Injectable } from '@angular/core';

/**
 * This transformation service builds on work from https://github.com/googollee/eviltransform/
 * It allows for transformation of for issues as described by
 * https://en.wikipedia.org/wiki/Restrictions_on_geographic_data_in_China#The_China_GPS_shift_problem
 *
 * @export
 * @class MapTransformService
 */
@Injectable({ providedIn: 'root' })
export class MapTransformService {
  private earthR = 6378137.0;

  private chinaPolygon = [
    [32.13573485059888, 79.00913394896361],
    [27.095442827751047, 99.22397769896361],
    [22.712477243876915, 100.43247379271361],
    [23.601368337056225, 105.68393863646361],
    [17.347624361757735, 109.33139957396361],
    [22.062351645370292, 121.98764957396361],
    [40.54480076968504, 125.15171207396361],
    [43.60993049250946, 130.6448761364636],
    [47.485379166595315, 134.5560089489636],
    [53.46001044858627, 121.72397769896361],
    [49.86428133646682, 118.88950504271361],
    [46.707570702362304, 119.57065738646361],
    [42.19362936648307, 109.50718082396361],
    [42.45355787753677, 96.41147769896361],
    [49.237059134638926, 86.65561832396361],
    [40.21002930132453, 75.84507144896361],
  ];

  /**
   * Return if coordinates are within the `China` region.
   * @param lat: number
   * @param lng: number
   */
  public outOfChina(lat: number, lng: number): boolean {
    return !this.pointInPolygon([lat, lng], this.chinaPolygon);
  }

  public transform(x, y) {
    const xy = x * y;
    const absX = Math.sqrt(Math.abs(x));
    const xPi = x * Math.PI;
    const yPi = y * Math.PI;
    const d = 20.0 * Math.sin(6.0 * xPi) + 20.0 * Math.sin(2.0 * xPi);

    let lat = d;
    let lng = d;

    lat += 20.0 * Math.sin(yPi) + 40.0 * Math.sin(yPi / 3.0);
    lng += 20.0 * Math.sin(xPi) + 40.0 * Math.sin(xPi / 3.0);

    lat += 160.0 * Math.sin(yPi / 12.0) + 320 * Math.sin(yPi / 30.0);
    lng += 150.0 * Math.sin(xPi / 12.0) + 300.0 * Math.sin(xPi / 30.0);

    lat *= 2.0 / 3.0;
    lng *= 2.0 / 3.0;

    lat += -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * xy + 0.2 * absX;
    lng += 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * xy + 0.1 * absX;

    return { lat: lat, lng: lng };
  }

  /**
   * Gets the difference between the points to where they should be
   *
   * @param lat
   * @param lng
   */
  public delta(lat, lng) {
    // eslint-disable-next-line @typescript-eslint/no-loss-of-precision
    const ee = 0.00669342162296594323;
    const d = this.transform(lng - 105.0, lat - 35.0);
    const radLat = (lat / 180.0) * Math.PI;
    let magic = Math.sin(radLat);
    magic = 1 - ee * magic * magic;
    const sqrtMagic = Math.sqrt(magic);
    d.lat = (d.lat * 180.0) / (((this.earthR * (1 - ee)) / (magic * sqrtMagic)) * Math.PI);
    d.lng = (d.lng * 180.0) / ((this.earthR / sqrtMagic) * Math.cos(radLat) * Math.PI);
    return d;
  }

  /**
   * Transforms from china positioning to normal positioning
   *
   * @param wgsLat
   * @param wgsLng
   */
  public wgs2gcj(wgsLat, wgsLng) {
    if (this.outOfChina(wgsLat, wgsLng)) {
      return { lat: wgsLat, lng: wgsLng };
    }
    const d = this.delta(wgsLat, wgsLng);
    return { lat: wgsLat + d.lat, lng: wgsLng + d.lng };
  }

  /**
   * Transforms from normal positioning to china positioning
   * @param gcjLat
   * @param gcjLng
   */
  public gcj2wgs(gcjLat, gcjLng) {
    if (this.outOfChina(gcjLat, gcjLng)) {
      return { lat: gcjLat, lng: gcjLng };
    }
    const d = this.delta(gcjLat, gcjLng);
    return { lat: gcjLat - d.lat, lng: gcjLng - d.lng };
  }

  /**
   * "Borrowed" from https://github.com/substack/point-in-polygon
   */
  private pointInPolygon(point, vs) {
    // ray-casting algorithm based on
    // http://www.ecse.rpi.edu/Homepages/wrf/Research/Short_Notes/pnpoly.html

    const x = point[0];
    const y = point[1];

    let inside = false;

    for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
      const xi = vs[i][0];
      const yi = vs[i][1];
      const xj = vs[j][0];
      const yj = vs[j][1];

      const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

      if (intersect) {
        inside = !inside;
      }
    }

    return inside;
  }
}
